'use client';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useAddToBasket } from '@hooks/basket';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import RatingStars from '@repo/ui/src/components/RatingStars';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import Image from 'next/image';
import { productToAnalytics } from '@repo/ui/src/services/analytics/factories';
import {
  productClicked,
  ProductClicked,
  productViewed,
} from '@repo/ui/src/services/analytics/segment';
import { ProductProps } from '@repo/ui/src/types';

interface LearnMoreProps {
  product: ProductProps;
}

const LearnMore = ({ product }: LearnMoreProps) => {
  const { t } = useTranslation();
  const { trigger } = useAddToBasket();
  const { close } = useModal(ModalID.learnMore);
  const { push, eCommEnabled, locale } = useCurrentUrl();

  const learnMoreClick = () => {
    const clickProps = {
      ...productToAnalytics(product, locale),
      list_name: 'learn more recipes',
    } as ProductClicked;
    productClicked(clickProps);
    push(product.productUrl);
    close();
  };

  const addToCartClick = async () => {
    productViewed(productToAnalytics(product, locale));
    await trigger([product]);
    close();
  };

  return (
    <div className="tw-pb-12 tw-px-16">
      {product?.mainImageUrl && (
        <div className="tw-mb-8 tw-flex tw-justify-center">
          <Image src={product?.mainImageUrl} alt={product?.title} width={280} height={280} />
        </div>
      )}

      <h2 className="tra-text-web-heading-4 tw-mb-2">{product?.title}</h2>

      {eCommEnabled && (
        <div className="tw-flex tw-gap-4 tw-mb-4 tw-items-center tw-justify-start">
          <RatingStars
            value={product?.ratingSummary?.average}
            reviewCount={product?.ratingSummary?.count}
            className="[&_svg]:tw-w-[18px]"
            hideValue
            showCountInParentheses
          />
          <span className="tw-self-stretch tw-border-l tw-border-warm-400" />
          <p itemProp="sku" className="tw-text-warm-400 tw-leading-none">
            {product?.sku}
          </p>
        </div>
      )}

      <p className="tra-text-web-body-l tw-mb-4">
        {product?.description || product?.shortDescription}
      </p>
      <p className="tra-text-web-heading-5 tw-mb-9">${product?.price}</p>

      <Button
        variant={ButtonVariant.secondary}
        size="large"
        className="tw-mb-4 tw-w-full"
        onClick={learnMoreClick}
      >
        {t('productDetailPageContent.learnMoreText', undefined, { default: 'Learn More' })}
      </Button>
      {product?.isOrderable && (
        <Button
          variant={ButtonVariant.primary}
          size="large"
          className="tw-w-full"
          onClick={addToCartClick}
        >
          {t('productDetailPageContent.addToCartText', undefined, { default: 'Add to Cart' })}
        </Button>
      )}
    </div>
  );
};

export default LearnMore;
